/*
 * Copyright 2021 Spotify AB
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React from 'react';
import { parseEntityRef } from '@backstage/catalog-model';
import { Link } from 'tmna-ui-kit';
import { entityRouteRef } from '@backstage/plugin-catalog-react';
import {Box, Grid, Typography} from '@material-ui/core';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import { TaskOutput } from '../../types';
import { useRouteRef } from '@backstage/core-plugin-api';

type TaskPageLinksProps = {
  output: TaskOutput;
  showTroubleShootlink:boolean;
};

export const TaskPageLinks = ({ output, showTroubleShootlink}: TaskPageLinksProps) => {
  const { entityRef: entityRefOutput, remoteUrl } = output;
  let { links = [] } = output;
  const entityRoute = useRouteRef(entityRouteRef);

  if (remoteUrl) {
    links = [{ url: remoteUrl, title: 'Go to repo' }, ...links];
  }

  if (entityRefOutput) {
    links = [
      {
        entityRef: entityRefOutput,
        title: 'Open service catalog',
      },
      ...links,
    ];
  }

  if(showTroubleShootlink){
    links = [
      ...links,
      { url: 'https://chofer.cloud.toyota.com/docs/default/Component/ACE-FAQ/chofer/#deploying-tech-docs-in-chofer', 
      title: 'Troubleshooting steps' },
    ];
  }
  


  return (
    <Box pt={7}>
      <Grid container direction="column" spacing={4}>
        {links
          .filter(({ url, entityRef }) => url || entityRef)
          .map(({ url, entityRef, title, icon }) => {
            if (entityRef) {
              const entityName = parseEntityRef(entityRef);
              const target = entityRoute(entityName);
              return { title, icon, url: target };
            }
            return { title, icon, url: url! };
          })
          .map(({ url, title }, i) => (
            <Grid item>
              <Link
                key={`output-link-${i}`}
                to={url}
                icon={<NavigateNextIcon color="secondary"/>}
                target="_blank"
              >
                <Typography variant="button">{title ?? url}</Typography>
              </Link>
            </Grid>
        ))}
      </Grid>
    </Box>
  );
};
